import React from "react";
import { SITE } from "@/lib/site";

export default function ConfidentialitePage() {
  return (
    <div data-testid="confidentialite-page">
      <section className="bg-black text-white py-24 lg:py-32">
        <div className="container-soft">
          <p className="overline text-white/60">RGPD</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight leading-[1.05]">
            Politique de confidentialité
          </h1>
          <p className="mt-6 max-w-2xl text-white/70">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-soft max-w-3xl">
          <div className="space-y-12 text-neutral-800">
            <p className="text-lg leading-relaxed">
              Pompes Funèbres Lumière des Nations s'engage à protéger la vie privée des familles
              qui nous confient leur peine. La présente politique précise comment nous collectons,
              utilisons et protégeons vos données personnelles, conformément au Règlement Général
              sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.
            </p>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">1. Responsable du traitement</h2>
              <p className="leading-relaxed">
                <strong className="font-medium">Pompes Funèbres Lumière des Nations</strong><br />
                {SITE.address}<br />
                Téléphone : <a href={SITE.phoneHref} className="underline">{SITE.phone}</a><br />
                Email : <a href={`mailto:${SITE.email}`} className="underline break-all">{SITE.email}</a>
              </p>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">2. Données collectées</h2>
              <p className="leading-relaxed">
                Lorsque vous utilisez notre formulaire de contact, nous collectons uniquement :
              </p>
              <ul className="mt-4 space-y-2 leading-relaxed list-disc pl-6">
                <li>Votre nom et prénom</li>
                <li>Votre adresse email</li>
                <li>Votre numéro de téléphone (facultatif)</li>
                <li>Le sujet et le contenu de votre message</li>
                <li>La date et l'heure de votre demande</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                Aucun cookie de suivi publicitaire n'est utilisé sur ce site.
              </p>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">3. Finalités du traitement</h2>
              <p className="leading-relaxed">Vos données sont utilisées exclusivement pour :</p>
              <ul className="mt-4 space-y-2 leading-relaxed list-disc pl-6">
                <li>Répondre à votre demande d'information ou de devis</li>
                <li>Vous accompagner dans l'organisation des obsèques</li>
                <li>Assurer le suivi administratif de notre relation</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                Vos données ne sont jamais vendues, louées ni cédées à des tiers à des fins commerciales.
              </p>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">4. Base légale</h2>
              <p className="leading-relaxed">
                Le traitement repose sur votre consentement (envoi du formulaire) et sur l'exécution
                de mesures précontractuelles prises à votre demande (article 6.1.a et 6.1.b du RGPD).
              </p>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">5. Durée de conservation</h2>
              <ul className="space-y-2 leading-relaxed list-disc pl-6">
                <li><strong className="font-medium">Demandes sans suite :</strong> 12 mois maximum après le dernier contact.</li>
                <li><strong className="font-medium">Dossiers clients :</strong> conservés conformément aux obligations légales et comptables (10 ans).</li>
              </ul>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">6. Destinataires des données</h2>
              <p className="leading-relaxed">
                Vos données sont accessibles uniquement aux personnes habilitées au sein de notre entreprise.
                Le cas échéant, elles peuvent être transmises à nos partenaires (officiants, marbriers,
                administrations) <em>uniquement</em> dans le cadre strict de l'organisation des obsèques que vous nous confiez.
              </p>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">7. Vos droits</h2>
              <p className="leading-relaxed">
                Conformément au RGPD, vous disposez à tout moment des droits suivants :
              </p>
              <ul className="mt-4 space-y-2 leading-relaxed list-disc pl-6">
                <li>Droit d'accès à vos données</li>
                <li>Droit de rectification</li>
                <li>Droit à l'effacement (« droit à l'oubli »)</li>
                <li>Droit à la limitation du traitement</li>
                <li>Droit à la portabilité de vos données</li>
                <li>Droit d'opposition</li>
                <li>Droit de retirer votre consentement à tout moment</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                Pour exercer ces droits, contactez-nous par email à
                {" "}<a href={`mailto:${SITE.email}`} className="underline break-all">{SITE.email}</a>{" "}
                ou par courrier à l'adresse de notre siège.
              </p>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">8. Réclamation auprès de la CNIL</h2>
              <p className="leading-relaxed">
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une
                réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
                <br />
                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="underline">www.cnil.fr</a>
              </p>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">9. Sécurité</h2>
              <p className="leading-relaxed">
                Nous mettons en œuvre les mesures techniques et organisationnelles appropriées pour
                garantir la confidentialité, l'intégrité et la disponibilité de vos données :
                connexion HTTPS, accès restreint, sauvegardes régulières.
              </p>
            </div>

            <div className="hairline" />

            <div>
              <h2 className="font-serif-display text-2xl mb-4">10. Cookies</h2>
              <p className="leading-relaxed">
                Ce site n'utilise pas de cookies de mesure d'audience ni de traceurs publicitaires.
                Seuls les cookies strictement nécessaires au fonctionnement (préférences techniques)
                peuvent être déposés ; ils ne nécessitent pas votre consentement préalable.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
